import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  CheckCircle2, AlertTriangle, Loader2, ShieldCheck,
  Building2, Banknote, Users, Mail,
} from "lucide-react";

// OCI V2.24B — compact, operator-friendly campaign routing & readiness summary.
// Read-only. Payment plumbing (Stripe keys/connect) lives in admin/charity surfaces,
// NOT here. The canonical beneficiary selector sits above this in campaign settings.

function Pill({ tone, children, testid }) {
  const cls = {
    green: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    slate: "bg-slate-100 text-slate-700",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <Badge className={`${cls} hover:${cls} gap-1`} data-testid={testid}>
      {tone === "green" ? <CheckCircle2 className="h-3 w-3" /> : tone === "rose" || tone === "amber" ? <AlertTriangle className="h-3 w-3" /> : null}
      {children}
    </Badge>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-700 flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" /> {label}
      </span>
      <div className="text-right">{children}</div>
    </div>
  );
}

export default function CampaignControlCentre({ campaignId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setErr(false);
    try {
      const res = await apiClient.get(`/campaigns/${campaignId}/control-centre`);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status !== 403) setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="control-centre-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading routing summary…
      </div>
    );
  }
  if (err || !data) return null;

  const pay = data.payment || {};
  const clarity = pay.ownershipClarity || {};
  const creds = pay.charityCredentials || {};
  const notify = data.notification || {};
  const readiness = data.readiness || {};
  const paymentStatus = readiness.checks?.payment?.status;
  const beneficiaryName = data.beneficiaryOrganisationName;

  // Payment route status → operator-friendly label.
  const routeView = paymentStatus === "ready"
    ? { tone: "green", label: "Live to beneficiary" }
    : clarity.isPlatformFallback
    ? { tone: "amber", label: "Fallback · not live" }
    : { tone: "rose", label: "Incomplete" };

  // OCI V2.32 — precise readiness label from the honest state machine.
  const READINESS_LABELS = {
    payment_ready: "Connected & live",
    payouts_disabled: "Payouts disabled",
    bacs_not_ready: "Direct Debit not ready",
    charges_disabled: "Charges disabled",
    requirements_due: "Stripe action needed",
    connected_onboarding_incomplete: "Onboarding incomplete",
    unknown_refresh_required: "Refresh required",
    not_connected: "Not connected",
  };
  const connState = creds.readinessState || (creds.configured ? "unknown_refresh_required" : "not_connected");
  const connLabel = READINESS_LABELS[connState] || "Not connected";
  const connTone = connState === "payment_ready" ? "green"
    : connState === "charges_disabled" ? "rose" : "amber";
  const lastSynced = creds.lastSyncedAt;

  // Comms sender path → friendly label.
  const emailSource = notify.email?.source;
  const commsView = !notify.email?.hasKey
    ? { tone: "amber", label: "Incomplete" }
    : (emailSource === "charity" || emailSource === "campaign_domain" || emailSource === "custom_resend")
    ? { tone: "green", label: "Charity sender" }
    : { tone: "slate", label: "Platform sender" };

  return (
    <div data-testid="campaign-control-centre" className="space-y-4">
      <div>
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-indigo-500" />
          Routing &amp; Readiness Summary
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Where this campaign's money, donor records and notifications go. Read-only — set the
          beneficiary charity above; manage payment connection from the charity dashboard.
        </p>
      </div>

      <Card className="p-4 border-slate-200" data-testid="cc-summary-card">
        <Row icon={Building2} label="Beneficiary charity">
          {beneficiaryName
            ? <span className="text-sm font-medium text-slate-900" data-testid="cc-beneficiary-name">{beneficiaryName}</span>
            : <Pill tone="amber" testid="cc-beneficiary-none">Not assigned</Pill>}
        </Row>
        <Row icon={Banknote} label="Payment route">
          <Pill tone={routeView.tone} testid="cc-route-status">{routeView.label}</Pill>
        </Row>
        <Row icon={Banknote} label="Charity payment connection">
          <div className="flex flex-col items-end gap-0.5">
            <Pill tone={connTone} testid="cc-connection-status">{connLabel}</Pill>
            {lastSynced && (
              <span className="text-[10px] text-slate-400" data-testid="cc-connection-last-synced">
                Checked {new Date(lastSynced).toLocaleDateString("en-GB")}
              </span>
            )}
          </div>
        </Row>
        <Row icon={Users} label="Donor destination">
          <span className="text-sm text-slate-700" data-testid="cc-donor-destination">Charity dashboard / CRM</span>
        </Row>
        <Row icon={Mail} label="Donor comms sender">
          <Pill tone={commsView.tone} testid="cc-comms-status">{commsView.label}</Pill>
        </Row>
      </Card>

      {(readiness.blockers || []).length > 0 && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="cc-readiness-blockers">
          <ul className="space-y-1">
            {readiness.blockers.map((b, i) => (
              <li key={i} className="text-xs text-rose-700 flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
